import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import PropTypes from 'prop-types'
import { useAuth } from './AuthContext'
import { listingsService } from '../services/listingsService'

// Create context
const FavoritesContext = createContext(null)

/** How long an un-hearted saved rental can be put back before it goes. */
export const UNDO_MS = 10000

function readLocalFavorites() {
  try {
    const stored = localStorage.getItem('favorites')
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

/**
 * Favorites provider. Two lists, one active at a time: logged-in users sync
 * against the backend Favorite table (saves follow the account across
 * devices); guests keep a localStorage list so hearts work before signup.
 * Deriving the active list (instead of resetting state on auth changes)
 * keeps the two modes from bleeding into each other.
 */
export function FavoritesProvider({ children }) {
  const { user } = useAuth()
  const [localFavorites, setLocalFavorites] = useState(readLocalFavorites)
  const [serverFavorites, setServerFavorites] = useState([])

  const favorites = user ? serverFavorites : localFavorites

  // Persist the guest list.
  useEffect(() => {
    localStorage.setItem('favorites', JSON.stringify(localFavorites))
  }, [localFavorites])

  // On login, load server favorites (IDs).
  useEffect(() => {
    if (!user) return undefined
    let active = true
    listingsService
      .getFavorites()
      .then(listings => {
        if (active) setServerFavorites(listings.map(l => l.id))
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [user])

  // Flip favorite status. Optimistic in both modes; logged-in users also
  // persist to the server and roll back if that fails.
  const flipFavorite = useCallback(
    listingId => {
      const flip = prev =>
        prev.includes(listingId)
          ? prev.filter(id => id !== listingId)
          : [...prev, listingId]

      if (user) {
        setServerFavorites(flip)
        listingsService.toggleFavorite(listingId).catch(() => {
          // Roll back the optimistic flip on failure.
          setServerFavorites(flip)
        })
      } else {
        setLocalFavorites(flip)
      }
    },
    [user]
  )

  // Removals held for UNDO_MS (un-hearting on the Saved page). The hold
  // lives here, not in the page, so leaving and coming back keeps the same
  // countdown. A held rental reads as not saved everywhere; hearting it
  // again anywhere cancels the hold.
  const [held, setHeld] = useState({}) // listingId -> removal time (ms)
  const timers = useRef({})
  const flipRef = useRef(flipFavorite)
  useEffect(() => {
    flipRef.current = flipFavorite
  }, [flipFavorite])

  const undoRemoval = useCallback(listingId => {
    clearTimeout(timers.current[listingId])
    delete timers.current[listingId]
    setHeld(prev => {
      if (!(listingId in prev)) return prev
      const next = { ...prev }
      delete next[listingId]
      return next
    })
  }, [])

  const holdRemoval = useCallback(
    listingId => {
      if (timers.current[listingId]) return
      const removeAt = Date.now() + UNDO_MS
      timers.current[listingId] = setTimeout(() => {
        undoRemoval(listingId)
        flipRef.current(listingId)
      }, UNDO_MS)
      setHeld(prev => ({ ...prev, [listingId]: removeAt }))
    },
    [undoRemoval]
  )

  const toggleFavorite = useCallback(
    listingId => {
      if (timers.current[listingId]) undoRemoval(listingId)
      else flipFavorite(listingId)
    },
    [flipFavorite, undoRemoval]
  )

  // Closing the site finishes what is held (best effort). Signing out or
  // switching accounts drops the holds and leaves those rentals saved.
  useEffect(() => {
    const finish = () => {
      for (const id of Object.keys(timers.current)) {
        clearTimeout(timers.current[id])
        flipRef.current(id)
      }
      timers.current = {}
    }
    window.addEventListener('pagehide', finish)
    return () => window.removeEventListener('pagehide', finish)
  }, [])
  useEffect(
    () => () => {
      for (const id of Object.keys(timers.current)) {
        clearTimeout(timers.current[id])
      }
      timers.current = {}
      setHeld({})
    },
    [user]
  )

  // Check if listing is favorited (a held removal reads as not saved).
  const isFavorite = useCallback(
    listingId => favorites.includes(listingId) && !(listingId in held),
    [favorites, held]
  )

  // Clear the active list
  const clearFavorites = useCallback(() => {
    if (user) setServerFavorites([])
    else setLocalFavorites([])
  }, [user])

  // Get count of favorites
  const favoritesCount = favorites.filter(id => !(id in held)).length

  const value = {
    favorites,
    toggleFavorite,
    isFavorite,
    pendingRemovals: held,
    holdRemoval,
    undoRemoval,
    clearFavorites,
    favoritesCount,
  }

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  )
}

FavoritesProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

// Custom hook
export function useFavorites() {
  const context = useContext(FavoritesContext)
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider')
  }
  return context
}

export default FavoritesContext
